import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { stripChapterPrefix } from '../lib/utils';
import ReportButton from '../components/ReportButton';

interface StoryPageProps {
  storyId: number;
  slug?: string;
  onNavigate: (page: string, data?: any) => void;
  user: any;
  onTitleReady?: (title: string) => void;
}

export default function StoryPage({ storyId, slug, onNavigate, user, onTitleReady }: StoryPageProps) {
  const [story, setStory] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [reviewBody, setReviewBody] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [readingListStatus, setReadingListStatus] = useState<string | null>(null);
  const [showLibraryMenu, setShowLibraryMenu] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [volumes, setVolumes] = useState<any[]>([]);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [relatedStories, setRelatedStories] = useState<any[]>([]);
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipAmount, setTipAmount] = useState<number>(10);
  const [tipLoading, setTipLoading] = useState(false);
  const [tipSuccess, setTipSuccess] = useState(false);
  const [tipError, setTipError] = useState('');
  const libraryRef = useRef<HTMLDivElement>(null);
  const shareRef = useRef<HTMLDivElement>(null);

  const handleShare = (platform: string) => {
    const url = `https://scribehaven.com/story/${story.slug || story.id}`;
    const text = `Check out "${story.title}" on ScribeHaven!`;
    if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'copy') {
      navigator.clipboard.writeText(url).then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      });
    }
    setShowShareMenu(false);
  };

  useEffect(() => {
    const fetchStory = slug
      ? api.stories.getBySlug(slug)
      : api.stories.get(storyId);

    fetchStory
      .then(data => {
        setStory(data);
        setFollowing(data.isFollowing);
        setReadingListStatus(data.readingListStatus || null);
        const authorName = data.penName?.penName || data.author?.username || 'Unknown';
        if (onTitleReady) onTitleReady(`${data.title} by ${authorName} — ScribeHaven`);
        // Load volumes for this story
        api.volumes.list(data.id)
          .then(setVolumes)
          .catch(() => setVolumes([]));
        // Load related stories
        api.discover.related(data.id)
          .then(setRelatedStories)
          .catch(() => setRelatedStories([]));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [storyId, slug]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (libraryRef.current && !libraryRef.current.contains(e.target as Node)) {
        setShowLibraryMenu(false);
      }
      if (shareRef.current && !shareRef.current.contains(e.target as Node)) {
        setShowShareMenu(false);
      }
    };
    if (showLibraryMenu || showShareMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLibraryMenu, showShareMenu]);

  const handleFollow = async () => {
    if (!user) return onNavigate('login');
    try {
      const res = await api.stories.follow(story.id);
      setFollowing(res.following);
      setStory((s: any) => ({
        ...s,
        _count: {
          ...s._count,
          follows: s._count.follows + (res.following ? 1 : -1)
        }
      }));
    } catch (err) {
      console.error('Follow failed:', err);
    }
  };

  const handleReadingList = async (status: string | null) => {
    if (!user) return onNavigate('login');
    try {
      await api.stories.readingList(story.id, status);
      setReadingListStatus(status);
      setShowLibraryMenu(false);
    } catch (err) {
      console.error('Reading list failed:', err);
    }
  };

  const READING_STATUSES = [
    { value: 'WANT_TO_READ', label: 'Want to Read' },
    { value: 'CURRENTLY_READING', label: 'Currently Reading' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'DROPPED', label: 'Dropped' },
  ];

  const handleTip = async () => {
    if (!user) return onNavigate('login');
    setTipLoading(true);
    setTipError('');
    try {
      await api.ink.tip(story.id, tipAmount);
      setTipSuccess(true);
      setTimeout(() => { setTipSuccess(false); setShowTipModal(false); setTipAmount(10); }, 2500);
    } catch (err: any) {
      setTipError(err?.message || 'Failed to send tip. Check your Ink balance.');
    } finally {
      setTipLoading(false);
    }
  };

  const handleReview = async () => {
    if (!user) return onNavigate('login');
    setSubmittingReview(true);
    try {
      const review = await api.reviews.create({ storyId: story.id, rating: reviewRating, body: reviewBody });
      setStory((s: any) => ({ ...s, reviews: [{ ...review, user }, ...s.reviews] }));
      setReviewBody('');
    } catch (err) {
      console.error('Review failed:', err);
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!story) return <div className="text-center py-32 text-gray-400">Story not found</div>;

  const gradient = ['from-purple-600 to-blue-600', 'from-rose-600 to-pink-600', 'from-emerald-600 to-teal-600'][story.id % 3];
  const storySlug = story.slug;

  return (
    <>
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="grid md:grid-cols-[240px_1fr] gap-8">
        <div className={`aspect-[6/9] rounded-xl bg-gradient-to-br ${gradient} overflow-hidden self-start relative`}>
          {story.coverUrl ? (
            <img src={story.coverUrl} alt={story.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center p-6">
              <span className="text-white/90 text-xl font-serif font-bold text-center">{story.title}</span>
            </div>
          )}
          {story.isMature && (
            <div className="absolute bottom-0 left-0 right-0 bg-red-900/90 backdrop-blur-sm px-3 py-2 flex items-center justify-center gap-2">
              <span className="text-base">🔞</span>
              <span className="text-red-200 text-xs font-bold tracking-wide uppercase">18+ Content Warning</span>
            </div>
          )}
        </div>

        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-3 py-1 bg-ink-600/20 text-ink-400 text-sm rounded-full font-semibold">{story.genre}</span>
            {story.tags?.map((tag: string) => (
              <button
                key={tag}
                onClick={() => onNavigate('discover', { tag })}
                className="px-3 py-1 bg-gray-800 text-gray-300 text-sm rounded-full font-medium hover:bg-ink-600/20 hover:text-ink-400 transition-colors cursor-pointer"
              >
                #{tag}
              </button>
            ))}
            {story.isMature && (
              <span className="px-3 py-1 bg-red-900/30 text-red-400 text-sm rounded-full font-medium">🔞 Mature</span>
            )}
          </div>

          <h1 className="text-3xl font-bold mb-1">{story.title}</h1>
          {story.penName ? (
            <button
              onClick={() => onNavigate('pen-profile', { penName: story.penName.penName })}
              className="text-ink-400 hover:underline mb-4 inline-block"
            >
              by {story.penName.penName}
            </button>
          ) : (
            <button
              onClick={() => onNavigate('profile', { username: story.author.username })}
              className="text-ink-400 hover:underline mb-4 inline-block"
            >
              by {story.author.username}
            </button>
          )}

          <p className="text-gray-300 leading-relaxed mb-6">{story.synopsis}</p>

          <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-6">
            <span>{story.totalViews.toLocaleString()} views</span>
            <span>{story._count.follows} followers</span>
            <span>{story.avgRating ? story.avgRating.toFixed(1) + ' ★' : 'No ratings'}</span>
            <span>{story.chapters.length} chapters</span>
            <span>{(story.totalWordCount || 0).toLocaleString()} words</span>
            <span className={`font-medium ${story.status === 'ONGOING' ? 'text-emerald-400' : story.status === 'COMPLETED' ? 'text-blue-400' : 'text-amber-400'}`}>
              {story.status}
            </span>
          </div>

          <div className="flex gap-3 mb-8">
            {story.chapters.length > 0 && (
              <button
                onClick={() => onNavigate('reader', { chapterId: story.chapters[0].id, storySlug, chapterNum: story.chapters[0].chapterNum })}
                className="px-5 py-2.5 bg-ink-600 hover:bg-ink-500 rounded-lg font-semibold transition-colors"
              >
                Start Reading
              </button>
            )}
            {user && user.id !== story.authorId && (
              <ReportButton targetType="STORY" targetId={story.id} targetLabel={story.title} variant="text" className="px-3 py-2.5" />
            )}
            <button
              onClick={handleFollow}
              className={`px-5 py-2.5 rounded-lg font-semibold transition-colors border ${
                following
                  ? 'bg-ink-600/20 border-ink-500 text-ink-400'
                  : 'bg-gray-800 border-gray-700 hover:border-ink-500'
              }`}
            >
              {following ? '✓ Following' : '+ Follow'}
            </button>
            <div className="relative" ref={libraryRef}>
              <button
                onClick={() => user ? setShowLibraryMenu(!showLibraryMenu) : onNavigate('login')}
                className={`px-5 py-2.5 rounded-lg font-semibold transition-colors border ${
                  readingListStatus
                    ? 'bg-emerald-600/20 border-emerald-500 text-emerald-400'
                    : 'bg-gray-800 border-gray-700 hover:border-emerald-500'
                }`}
              >
                {readingListStatus
                  ? READING_STATUSES.find(s => s.value === readingListStatus)?.label || 'In Library'
                  : '+ Library'}
              </button>
              {showLibraryMenu && (
                <div className="absolute top-12 left-0 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden min-w-[180px]">
                  {READING_STATUSES.map(s => (
                    <button
                      key={s.value}
                      onClick={() => handleReadingList(s.value)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-800 transition-colors ${
                        readingListStatus === s.value ? 'text-emerald-400 bg-gray-800/50' : 'text-gray-300'
                      }`}
                    >
                      {readingListStatus === s.value && '✓ '}{s.label}
                    </button>
                  ))}
                  {readingListStatus && (
                    <>
                      <div className="border-t border-gray-700" />
                      <button
                        onClick={() => handleReadingList(null)}
                        className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-gray-800 transition-colors"
                      >
                        Remove from Library
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {/* Tip Author Button */}
            {user && user.id !== story.authorId && (
              <button
                onClick={() => setShowTipModal(true)}
                className="px-4 py-2.5 rounded-lg font-semibold transition-colors border bg-amber-900/20 border-amber-700/50 text-amber-400 hover:bg-amber-900/40 flex items-center gap-2"
                title="Tip this author with Ink"
              >
                <span className="text-base">✦</span>
                Tip
              </button>
            )}
            {/* Share Button */}
            <div className="relative" ref={shareRef}>
              <button
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="px-4 py-2.5 rounded-lg font-semibold transition-colors border bg-gray-800 border-gray-700 hover:border-gray-500 flex items-center gap-2"
                title="Share this story"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share
              </button>
              {showShareMenu && (
                <div className="absolute top-12 right-0 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden min-w-[180px]">
                  <button
                    onClick={() => handleShare('twitter')}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-800 transition-colors text-gray-300 flex items-center gap-3"
                  >
                    <span className="font-bold text-base">𝕏</span> Share on X/Twitter
                  </button>
                  <button
                    onClick={() => handleShare('facebook')}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-800 transition-colors text-gray-300 flex items-center gap-3"
                  >
                    <span className="text-blue-400 font-bold">f</span> Share on Facebook
                  </button>
                  <div className="border-t border-gray-700" />
                  <button
                    onClick={() => handleShare('copy')}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-800 transition-colors text-gray-300 flex items-center gap-3"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {copySuccess ? '✓ Copied!' : 'Copy Link'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <section className="mt-10">
        <h2 className="text-xl font-bold mb-4">Chapters ({story.chapters.length})</h2>
        {volumes.length > 0 ? (
          <div className="space-y-6">
            {volumes.map((vol: any) => (
              <div key={vol.id}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-px flex-1 bg-gray-800" />
                  <div className="text-center">
                    <span className="text-xs font-semibold uppercase tracking-widest text-ink-400 px-3">
                      {vol.title}
                    </span>
                    {vol.description && (
                      <p className="text-xs text-gray-500 mt-0.5">{vol.description}</p>
                    )}
                  </div>
                  <div className="h-px flex-1 bg-gray-800" />
                </div>
                <div className="space-y-2">
                  {vol.chapters.map((ch: any) => (
                    <button
                      key={ch.id}
                      onClick={() => onNavigate('reader', { chapterId: ch.id, storySlug, chapterNum: ch.chapterNum })}
                      className="w-full text-left p-4 bg-gray-900 border border-gray-800 rounded-lg hover:border-ink-500/50 transition-colors flex items-center justify-between"
                    >
                      <div>
                        <span className="text-gray-500 text-sm mr-3">Ch. {ch.chapterNum}</span>
                        <span className="font-medium">{stripChapterPrefix(ch.title)}</span>
                      </div>
                      <div className="text-sm text-gray-500 flex gap-4">
                        <span>{ch.wordCount?.toLocaleString()} words</span>
                        {ch.isPremium && <span className="text-amber-400">★ Premium</span>}
                        <span>{new Date(ch.publishedAt).toLocaleDateString()}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {/* Unassigned chapters */}
            {(() => {
              const assignedIds = new Set(volumes.flatMap((v: any) => v.chapters.map((c: any) => c.id)));
              const unassigned = story.chapters.filter((ch: any) => !assignedIds.has(ch.id));
              if (unassigned.length === 0) return null;
              return (
                <div>
                  {volumes.length > 0 && (
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-px flex-1 bg-gray-800" />
                      <span className="text-xs font-semibold uppercase tracking-widest text-gray-500 px-3">More Chapters</span>
                      <div className="h-px flex-1 bg-gray-800" />
                    </div>
                  )}
                  <div className="space-y-2">
                    {unassigned.map((ch: any) => (
                      <button
                        key={ch.id}
                        onClick={() => onNavigate('reader', { chapterId: ch.id, storySlug, chapterNum: ch.chapterNum })}
                        className="w-full text-left p-4 bg-gray-900 border border-gray-800 rounded-lg hover:border-ink-500/50 transition-colors flex items-center justify-between"
                      >
                        <div>
                          <span className="text-gray-500 text-sm mr-3">Ch. {ch.chapterNum}</span>
                          <span className="font-medium">{stripChapterPrefix(ch.title)}</span>
                        </div>
                        <div className="text-sm text-gray-500 flex gap-4">
                          <span>{ch.wordCount.toLocaleString()} words</span>
                          {ch.isPremium && <span className="text-amber-400">★ Premium</span>}
                          <span>{new Date(ch.publishedAt).toLocaleDateString()}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="space-y-2">
            {story.chapters.map((ch: any) => (
              <button
                key={ch.id}
                onClick={() => onNavigate('reader', { chapterId: ch.id, storySlug, chapterNum: ch.chapterNum })}
                className="w-full text-left p-4 bg-gray-900 border border-gray-800 rounded-lg hover:border-ink-500/50 transition-colors flex items-center justify-between"
              >
                <div>
                  <span className="text-gray-500 text-sm mr-3">Ch. {ch.chapterNum}</span>
                  <span className="font-medium">{stripChapterPrefix(ch.title)}</span>
                </div>
                <div className="text-sm text-gray-500 flex gap-4">
                  <span>{ch.wordCount.toLocaleString()} words</span>
                  {ch.isPremium && <span className="text-amber-400">★ Premium</span>}
                  <span>{new Date(ch.publishedAt).toLocaleDateString()}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-bold mb-4">Reviews ({story.reviews?.length || 0})</h2>

        {user && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-gray-400">Your rating:</span>
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setReviewRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  className={`text-2xl transition-all duration-150 ${
                    n <= (hoverRating || reviewRating)
                      ? 'text-amber-400 drop-shadow-[0_0_6px_rgba(251,191,36,0.5)] scale-110'
                      : 'text-gray-600 hover:text-gray-500'
                  }`}
                >
                  {n <= (hoverRating || reviewRating) ? '★' : '☆'}
                </button>
              ))}
            </div>
            <textarea
              value={reviewBody}
              onChange={e => setReviewBody(e.target.value)}
              placeholder="Write your review..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-sm resize-none h-24 focus:border-ink-500 focus:outline-none"
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={handleReview}
                disabled={!reviewBody.trim() || submittingReview}
                className="px-4 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
              >
                Submit Review
              </button>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {story.reviews?.map((review: any) => (
            <div key={review.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold">
                    {review.user?.username?.[0]?.toUpperCase() || '?'}
                  </div>
                  <span className="font-medium text-sm">{review.user?.username}</span>
                  <span className="text-amber-400 text-sm">{'★'.repeat(Math.round(review.rating))}</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => api.reviews.upvote(review.id)}
                    className="text-sm text-gray-500 hover:text-ink-400 transition-colors"
                  >
                    ▲ {review.upvotes}
                  </button>
                  {user && user.id !== review.user?.id && (
                    <ReportButton targetType="REVIEW" targetId={review.id} targetLabel={`Review by ${review.user?.username}`} variant="icon" />
                  )}
                </div>
              </div>
              <p className="text-gray-300 text-sm">{review.body}</p>
            </div>
          ))}
        </div>
      </section>

      {relatedStories.length > 0 && (
        <section className="mt-10">
          <h2 className="text-xl font-bold mb-4">More in {story.genre}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {relatedStories.map((rs: any) => (
              <button
                key={rs.id}
                onClick={() => onNavigate('story', { id: rs.id, slug: rs.slug })}
                className="group text-left"
              >
                <div className="aspect-[2/3] rounded-lg overflow-hidden mb-2 bg-gradient-to-br from-ink-700 to-purple-800">
                  {rs.coverUrl ? (
                    <img
                      src={rs.coverUrl}
                      alt={rs.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center p-2">
                      <span className="text-xs font-bold text-center leading-tight text-white/80">{rs.title.slice(0, 30)}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs font-medium text-white truncate group-hover:text-ink-400 transition-colors">{rs.title}</p>
                <p className="text-xs text-gray-500 truncate">{rs.penName?.penName || rs.author?.username}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-gray-600">{rs._count?.chapters || 0} ch</span>
                  <span className="text-xs text-gray-600">{rs._count?.follows || 0} followers</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>

    {/* Tip Author Modal */}
    {/* Fragment wrapper opened above */}
    {showTipModal && (
      <div className="fixed inset-0 z-50 bg-gray-950/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => { setShowTipModal(false); setTipError(''); setTipSuccess(false); }}>
        <div className="bg-gray-900 border border-gray-700 rounded-2xl max-w-sm w-full p-8" onClick={e => e.stopPropagation()}>
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-900/30 mb-3">
              <span className="text-3xl">✦</span>
            </div>
            <h2 className="text-xl font-bold">Tip the Author</h2>
            <p className="text-gray-400 text-sm mt-1">Show your appreciation for <span className="text-white font-medium">{story.penName?.penName || story.author?.username}</span></p>
          </div>
          {tipSuccess ? (
            <div className="text-center py-6">
              <div className="text-5xl mb-3">🎉</div>
              <p className="text-emerald-400 font-semibold text-lg">Tip sent!</p>
              <p className="text-gray-400 text-sm mt-1">{tipAmount} Ink delivered to the author.</p>
            </div>
          ) : (
            <>
              <div className="flex gap-3 justify-center mb-6">
                {[10, 50, 100].map(amt => (
                  <button
                    key={amt}
                    onClick={() => setTipAmount(amt)}
                    className={`flex-1 py-3 rounded-xl font-bold text-lg border-2 transition-all ${
                      tipAmount === amt
                        ? 'bg-amber-900/40 border-amber-500 text-amber-400 scale-105'
                        : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-amber-700'
                    }`}
                  >
                    <span className="text-amber-400">✦</span> {amt}
                  </button>
                ))}
              </div>
              {tipError && <p className="text-red-400 text-sm text-center mb-4">{tipError}</p>}
              <button
                onClick={handleTip}
                disabled={tipLoading}
                className="w-full py-3 bg-amber-600 hover:bg-amber-500 rounded-xl font-bold text-gray-950 transition-colors disabled:opacity-50"
              >
                {tipLoading ? 'Sending...' : `Send ${tipAmount} Ink`}
              </button>
              <button
                onClick={() => { setShowTipModal(false); setTipError(''); }}
                className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-gray-300 transition-colors"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    )}
    </>
  );
}
